import { useCallback, useEffect, useRef, useState } from 'react';
import { useApolloClient } from '@apollo/client';
import {
  BlueprintUpdatesDocument,
  BlueprintVersionDocument,
  type BlueprintVersionQueryData,
  useBlueprintDownloadMutation,
  type BlueprintFieldsFragment,
  type BlueprintUpdatesQueryData,
} from '@/generated/graphql';
import { useElectron } from '@/context/ElectronContext';
import { useTransferManagerContext } from '@/context/TransferManagerContext';
import { db, type LocalBlueprint } from '@/common/utils/localDb';
import {
  blueprintFileName,
  reserveBlueprintDownloadPath,
} from './blueprintDownloadPath';
import { verifyDownloadedFile } from '@/common/utils/fileIntegrity';
import { getBlueprintFileError } from '@/common/utils/blueprintFilePolicy';

const activeDownloads = new Set<string>();
type Update = BlueprintUpdatesQueryData['blueprintUpdates'][number];
export function useBlueprintLibrary() {
  const { isElectron, electronAPI } = useElectron();
  const client = useApolloClient();
  const { enqueueDownload } = useTransferManagerContext();
  const [getDownload] = useBlueprintDownloadMutation();
  const [copies, setCopies] = useState<LocalBlueprint[]>([]);
  const [updates, setUpdates] = useState<Record<string, Update>>({});
  const [updateError, setUpdateError] = useState('');
  const [busy, setBusy] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [refreshError, setRefreshError] = useState('');
  const [folder, setFolder] = useState('');
  const [hydrated, setHydrated] = useState(false);
  const [pendingRefreshes, setPendingRefreshes] = useState(0);
  const generation = useRef(0);
  const [replacement, setReplacement] = useState<{
    blueprint: BlueprintFieldsFragment;
    versionId: string;
    old: LocalBlueprint;
    version: number;
  }>();
  const refresh = useCallback(async () => {
    if (!isElectron || !electronAPI?.getBlueprintDirectory) return;
    const sequence = ++generation.current;
    setPendingRefreshes((count) => count + 1);
    try {
      setFolder(await electronAPI.getBlueprintDirectory());
      const records = await db.blueprints.toArray();
      const present = (
        await Promise.all(
          records.map(async (record) => {
            // Keep every version and every pre-existing path in place.
            const info = await electronAPI.getFileInfo(record.path);
            if (!info.success && !info.error?.includes('ENOENT'))
              throw new Error(
                info.error || 'Could not inspect local Blueprints.'
              );
            return info.success && info.info?.isFile ? record : null;
          })
        )
      ).filter((r): r is LocalBlueprint => !!r);
      if (sequence !== generation.current) return;
      setCopies(present);
      setRefreshError('');
      const ids = [...new Set(present.map((r) => r.blueprintId || r.id))];
      try {
        const statuses: Update[] = [];
        for (let offset = 0; offset < ids.length; offset += 100) {
          const result = await client.query({
            query: BlueprintUpdatesDocument,
            variables: { ids: ids.slice(offset, offset + 100) },
            fetchPolicy: 'network-only',
          });
          statuses.push(...result.data.blueprintUpdates);
        }
        if (sequence === generation.current) {
          setUpdates(
            Object.fromEntries(statuses.map((s) => [s.blueprintId, s]))
          );
          setUpdateError('');
        }
      } catch {
        if (sequence === generation.current) {
          setUpdates({});
          setUpdateError(
            'Could not check for updates. Your downloaded files are still available.'
          );
        }
      }
    } catch (e) {
      if (sequence === generation.current)
        setRefreshError(e instanceof Error ? e.message : String(e));
    } finally {
      setHydrated(true);
      setPendingRefreshes((count) => count - 1);
    }
  }, [isElectron, electronAPI, client]);
  useEffect(() => {
    const refreshLibrary = () => {
      void refresh().catch((e) => setError(String(e)));
    };
    refreshLibrary();
    window.addEventListener('focus', refreshLibrary);
    window.addEventListener('blueprint-library-changed', refreshLibrary);
    return () => {
      window.removeEventListener('focus', refreshLibrary);
      window.removeEventListener('blueprint-library-changed', refreshLibrary);
    };
  }, [refresh]);
  const downloaded = Object.fromEntries(
    [...copies]
      .sort((a, b) => (a.version || 1) - (b.version || 1))
      .map((r) => [r.blueprintId || r.id, r])
  );
  const outdated = Object.keys(downloaded).filter(
    (id) =>
      updates[id]?.available &&
      !copies.some(
        (r) =>
          (r.blueprintId || r.id) === id &&
          r.revisionId === updates[id].versionId
      )
  );
  const performDownload = async (
    blueprint: BlueprintFieldsFragment,
    versionId: string,
    old?: LocalBlueprint
  ) => {
    if (activeDownloads.has(blueprint._id)) return;
    activeDownloads.add(blueprint._id);
    setBusy((ids) => [...ids, blueprint._id]);
    setError('');
    let releasePath: (() => void) | undefined;
    try {
      const requestId = crypto.randomUUID();
      const variables = { id: blueprint._id, requestId, versionId };
      const file = (await getDownload({ variables })).data?.blueprintDownload;
      if (!file || file.revisionId !== versionId)
        throw new Error('Could not prepare this version.');
      const reason = getBlueprintFileError(file.fileName, '', file.size);
      if (reason) throw new Error(reason);
      if (isElectron) {
        if (!electronAPI?.getBlueprintDirectory)
          throw new Error('Update the desktop app to download Blueprints.');
        if (old && !electronAPI.removeSupersededBlueprint)
          throw new Error(
            'Update the desktop app to replace a download, or choose Keep both.'
          );
        const { data } = await client.query<BlueprintVersionQueryData>({
          query: BlueprintVersionDocument,
          variables: { id: blueprint._id, versionId },
          fetchPolicy: 'network-only',
        });
        const release = data.blueprintVersion;
        if (
          release._id !== versionId ||
          release.version !== file.version ||
          !release.sharingEnabled
        )
          throw new Error('This version is no longer available.');
        const root = await electronAPI.getBlueprintDirectory();
        const reserved = await reserveBlueprintDownloadPath(
          root,
          blueprint.creatorName,
          blueprint.title,
          electronAPI.getFileInfo,
          file.fileName,
          file.version
        );
        releasePath = reserved.release;
        const result = await enqueueDownload({
          subject: {
            kind: 'blueprint',
            id: `${blueprint._id}:${versionId}`,
            label: `${blueprint.title} v${file.version}`,
          },
          files: [
            {
              fileId: versionId,
              fileName: reserved.relative,
              size: file.size,
              fullHash: file.fullHash,
            },
          ],
          dirHandle: root,
          resolveDownloadUrl: async () => {
            const fresh = (await getDownload({ variables })).data
              ?.blueprintDownload;
            if (
              !fresh ||
              fresh.revisionId !== versionId ||
              fresh.fullHash !== file.fullHash
            )
              throw new Error('This version is no longer available.');
            return fresh.presignedUrl;
          },
        });
        if (!result)
          throw new Error(
            'Download did not complete. Your existing copy is unchanged.'
          );
        const path = `${root}/${reserved.relative}`;
        await db.blueprints.put({
          blueprint: {
            ...blueprint,
            version: file.version,
            revisionId: versionId,
            pluginName: release.pluginName,
            pluginVersion: release.pluginVersion,
            dawVersion: release.dawVersion,
            requirements: release.requirements,
            stockOnly: release.stockOnly,
            fileName: file.fileName,
            sharingEnabled: release.sharingEnabled,
            waveForm: release.waveForm,
            thumbnailUrl: '',
            previewUrl: '',
          },
          id: `${blueprint._id}:${versionId}`,
          blueprintId: blueprint._id,
          title: blueprint.title,
          version: file.version,
          revisionId: versionId,
          fullHash: file.fullHash,
          path,
          downloadedAt: new Date(),
        });
        if (old) {
          try {
            await electronAPI.removeSupersededBlueprint!(
              old.path,
              old.fullHash,
              path,
              file.fullHash
            );
            await db.blueprints.delete(old.id);
          } catch (e) {
            setError(
              `New version saved. Both copies were kept: ${e instanceof Error ? e.message : String(e)}`
            );
          }
        }
        window.dispatchEvent(new Event('blueprint-library-changed'));
      } else {
        const response = await fetch(file.presignedUrl);
        if (!response.ok) throw new Error('Download failed. Please retry.');
        const blob = await response.blob();
        await verifyDownloadedFile(blob, {
          fileName: file.fileName,
          size: file.size,
          fullHash: file.fullHash,
        });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = blueprintFileName(
          blueprint.title,
          file.fileName,
          file.version
        );
        anchor.click();
        setTimeout(() => URL.revokeObjectURL(url), 1000);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Download failed');
    } finally {
      releasePath?.();
      activeDownloads.delete(blueprint._id);
      setBusy((ids) => ids.filter((id) => id !== blueprint._id));
    }
  };
  const download = async (
    blueprint: BlueprintFieldsFragment,
    selectedVersionId?: string,
    selectedVersion?: number
  ) => {
    const versionId =
      selectedVersionId ||
      updates[blueprint._id]?.versionId ||
      blueprint.revisionId;
    const version =
      selectedVersion ||
      updates[blueprint._id]?.version ||
      blueprint.version ||
      1;
    const local = copies.filter(
      (r) => (r.blueprintId || r.id) === blueprint._id
    );
    const same = local.find((r) => r.revisionId === versionId);
    if (same) {
      await electronAPI?.showItemInFolder(same.path);
      return;
    }
    const older = local
      .filter((r) => (r.version || 1) < version)
      .sort((a, b) => (b.version || 1) - (a.version || 1))[0];
    if (isElectron && older)
      setReplacement({ blueprint, versionId, version, old: older });
    else await performDownload(blueprint, versionId);
  };
  const record = (id: string, versionId?: string) =>
    versionId
      ? copies.find(
          (r) => (r.blueprintId || r.id) === id && r.revisionId === versionId
        )
      : downloaded[id];
  return {
    isElectron,
    downloaded,
    copies,
    updates,
    outdated,
    updateError,
    loading: !!(
      isElectron &&
      electronAPI?.getBlueprintDirectory &&
      (!hydrated || pendingRefreshes > 0)
    ),
    busy,
    error: error || refreshError,
    setError,
    folder,
    download,
    refresh,
    replacement,
    cancelReplacement: () => setReplacement(undefined),
    confirmReplacement: async (replace: boolean) => {
      const pending = replacement;
      setReplacement(undefined);
      if (pending)
        await performDownload(
          pending.blueprint,
          pending.versionId,
          replace ? pending.old : undefined
        );
    },
    reveal: async (id: string, versionId?: string) => {
      const r = record(id, versionId);
      if (r) await electronAPI?.showItemInFolder(r.path);
    },
    drag: (id: string, versionId?: string) => {
      const r = record(id, versionId);
      if (r) electronAPI?.startBlueprintDrag?.(r.path);
    },
  };
}
